// TriggerMgr + Trigger + the shared condition/action parameter machinery — the mission
// scripting engine. Triggers load from the level's [Triggers] section ("T" starts a
// trigger and carries its per-side eval-order assignments; "C"/"A" props are its
// conditions/actions), evaluate per side in per-side order every TriggerMgr::Update
// (pumped every 6 sim updates via the ScheduleUpdateTriggers event), and run their
// action list until an action "blocks" (returns false) — the block point is remembered
// per side and resumed next Update. Firing disarms the trigger for that side unless a
// PreserveTriggerAction re-arms it. game/triggermgr.cpp (all 716 lines; the
// DEBUG_HELPERS viewer is excluded).

import type { SimContext } from '../context.ts';
import { IniReader, IniScanf } from '../data/ini.ts';
import { kcSides, ksideNeutral, ksidmAll } from '../player/side.ts';
import { GetSideMask } from '../types.ts';
import type { Player } from '../player/player.ts';
import type { Condition } from './triggerconditions.ts';
import { LoadCondition } from './triggerconditions.ts';
import type { TriggerAction } from './triggeractions.ts';
import { LoadAction } from './triggeractions.ts';

// game/ht.h:4929-4933
export const kcTriggersPerSide = 128;
export const kcSwitchMax = 16;
export const kcTriggerTimersMax = 16;
export const kctTimerNotStarted = -100000;

// game/res.h:598-600
export const knQualifierAtLeast = 0;
export const knQualifierAtMost = 1;
export const knQualifierExactly = 2;

// game/res.h:603-610 — "condition/action side" encoding (bit indices into a word)
export const knCaSideSide1 = 1;
export const knCaSideEnemies = 5;
export const knCaSideAllies = 6;
export const knCaSideAllSides = 7;
export const knCaSideCurrentSide = 8;

// game/res.h:625-638
export const knWinLoseTypeWin = 1;
export const knWinLoseTypeLose = 2;
export const knMoreCloseTypeMore = 1;

// game/res.h:663-666
export const knModifyNumberTypeSet = 1;
export const knModifyNumberTypeAdd = 2;
export const knModifyNumberTypeSubtract = 3;

// game/res.h:670-688 — Conditions
export const knMissionLoadedCondition = 0;
export const knCreditsCondition = 1;
export const knAreaContainsUnitsCondition = 2;
export const knGalaxiteCapacityReachedCondition = 3;
export const knPlaceStructureModeCondition = 5;
export const knElapsedTimeCondition = 6;
export const knOwnsUnitsCondition = 7;
export const knMinerCantFindGalaxiteCondition = 8;
export const knSwitchCondition = 11;
export const knPeriodicTimerCondition = 12;
export const knDiscoversSideCondition = 13;
export const knCountdownTimerCondition = 14;
export const knTestPvarCondition = 16;
export const knHasUpgradesCondition = 17;
export const knConditionMax = 18;

// game/res.h:692-731 — TriggerActions
export const knSetAllowedUnitsTriggerAction = 1;
export const knEcomTriggerAction = 2;
export const knSetObjectiveTriggerAction = 3;
export const knSetNextMissionTriggerAction = 4;
export const knEndMissionTriggerAction = 5;
export const knWaitTriggerAction = 6;
export const knSetSwitchTriggerAction = 7;
export const knPreserveTriggerTriggerAction = 9;
export const knCenterViewTriggerAction = 10;
export const knDefogAreaTriggerAction = 12;
export const knCreateUnitGroupTriggerAction = 15;
export const knHuntTriggerAction = 16;
export const knCreateRandomUnitGroupTriggerAction = 17;
export const knAlliesTriggerAction = 18;
export const knStartCountdownTimerTriggerAction = 19;
export const knModifyCountdownTimerTriggerAction = 20;
export const knRepairTriggerAction = 21;
export const knEnableReplicatorTriggerAction = 22;
export const knModifyCreditsTriggerAction = 23;
export const knMoveUnitsInAreaTriggerAction = 25;
export const knSetFormalObjectiveTextTriggerAction = 26;
export const knSetFormalObjectiveStatusTriggerAction = 27;
export const knShowObjectivesTriggerAction = 28;
export const knSetFormalObjectiveInfoTriggerAction = 29;
export const knCutSceneTriggerAction = 30;
export const knJumpToMissionTriggerAction = 31;
export const knModifyPvarTriggerAction = 32;
export const knSetPvarTextTriggerAction = 33;
export const knShowAlertTriggerAction = 34;
export const knSetAllowedUpgradesTriggerAction = 35;
export const knSetUpgradesTriggerAction = 36;

// game/ht.h:2309-2310
export const kcbPvarNameMax = 21;

// game/ht.h:4070-4071 — CountdownTimer flags
export const kfCtVisibleAtStart = 1;
export const kfCtRunning = 2;

// game/ht.h:6143
export const knAreaLastDiscovery = -1;

//
// Condition/Action parameter types (triggermgr.cpp:462-487)
//

export class QualifiedNumber {
  m_nQualifier = 0;
  m_nNumber = 0;

  // triggermgr.cpp:462-468
  Parse(pp: { psz: string }): boolean {
    const r = IniScanf(pp.psz, '%d,%ld,%+');
    if (r.count >= 3) pp.psz = pp.psz.slice(r.values[2] as number);
    if (r.count < 2) return false;
    this.m_nQualifier = r.values[0] as number;
    this.m_nNumber = r.values[1] as number;
    return true;
  }

  // triggermgr.cpp:470-487
  Compare(nNumber: number): boolean {
    switch (this.m_nQualifier) {
      case knQualifierAtLeast:
        return nNumber >= this.m_nNumber;
      case knQualifierAtMost:
        return nNumber <= this.m_nNumber;
      case knQualifierExactly:
        return nNumber === this.m_nNumber;
    }
    return false;
  }
}

// triggermgr.cpp:637-668 — %d then either advance past the comma or consume the tail
export function ParseNumber(pp: { psz: string }, out: { n: number }): boolean {
  const r = IniScanf(pp.psz, '%d,%+');
  if (r.count >= 1) out.n = r.values[0] as number;
  if (r.count === 1) pp.psz = '';
  else if (r.count >= 2) pp.psz = pp.psz.slice(r.values[1] as number);
  return r.count >= 1;
}

export const ParseLong = ParseNumber; // %ld == %d in JS numbers (triggermgr.cpp:653-668)

// triggermgr.cpp:670-677
export function ParseArea(pp: { psz: string }, out: { n: number }): boolean {
  return ParseNumber(pp, out);
}

// triggermgr.cpp:679-686
export function ParseUnitMask(pp: { psz: string }, out: { n: number }): boolean {
  out.n = 0;
  const r = IniScanf(pp.psz, '%d,%+');
  if (r.count >= 1) out.n = r.values[0] as number;
  if (r.count >= 2) pp.psz = pp.psz.slice(r.values[1] as number);
  return r.count >= 1;
}

// triggermgr.cpp:688-696
export function ParseUpgradeMask(pp: { psz: string }, out: { n: number }): boolean {
  const r = IniScanf(pp.psz, '%d,%+');
  if (r.count >= 1) out.n = (r.values[0] as number) & 0xffff;
  if (r.count >= 2) pp.psz = pp.psz.slice(r.values[1] as number);
  return r.count >= 1;
}

// triggermgr.cpp:698-713
export function ParseString(pp: { psz: string }, out: { s: string }): boolean {
  const r = IniScanf(pp.psz, '%s,%+');
  if (r.count >= 1) out.s = r.values[0] as string;
  if (r.count === 1) pp.psz = '';
  else if (r.count >= 2) pp.psz = pp.psz.slice(r.values[1] as number);
  return r.count >= 1;
}

// triggermgr.cpp:595-619 — expand a CaSideMask word into a SideMask for the current side
export function GetSideMaskFromCaSideMask(
  ctx: SimContext,
  sideCur: number,
  wfCaSideMask: number,
): number {
  let sidm = wfCaSideMask & ksidmAll;
  if (wfCaSideMask & (1 << knCaSideAllSides)) {
    sidm |= ksidmAll;
  } else {
    if (wfCaSideMask & (1 << knCaSideAllies)) {
      sidm |= ctx.plrm.GetPlayer(sideCur)!.GetAllies();
    }
    if (wfCaSideMask & (1 << knCaSideEnemies)) {
      sidm |= ksidmAll & ~ctx.plrm.GetPlayer(sideCur)!.GetAllies();
    }
    if (wfCaSideMask & (1 << knCaSideCurrentSide)) sidm |= GetSideMask(sideCur);
  }
  return sidm;
}

// triggermgr.cpp:621-636
export function GetPlayersListFromCaSideMask(
  ctx: SimContext,
  sideCur: number,
  wfMask: number,
): Player[] {
  const sidm = GetSideMaskFromCaSideMask(ctx, sideCur, wfMask);
  const applr: Player[] = [];
  for (
    let pplr = ctx.plrm.GetNextPlayer(null);
    pplr !== null;
    pplr = ctx.plrm.GetNextPlayer(pplr)
  ) {
    if (sidm & GetSideMask(pplr.GetSide())) applr.push(pplr);
  }
  return applr;
}

//
// CountdownTimer (triggermgr.cpp:489-587) — m_secs/m_tLast/m_wf are SIM state (the
// CountdownTimerCondition reads them); the on-screen label is host-side
//

export class CountdownTimer {
  private ctx: SimContext;
  m_szFormat = '';
  m_secs = 0;
  m_tLast = 0;
  m_wf = 0;
  /** The on-screen control's visibility (kfCtlVisible): the C++ SaveState refreshes
   *  kfCtVisibleAtStart from the LIVE control (triggermgr.cpp:516-518) and the SimUI
   *  re-shows from the bit on load (SimUI.cpp:331). Headless, ShowTimer is the only
   *  visibility writer, so tracking it here reproduces the saved bit exactly
   *  (sim-quirks B14). */
  m_fVisible = false;

  constructor(ctx: SimContext) {
    this.ctx = ctx;
  }

  // triggermgr.cpp:495-526 (knVerCountdownTimerState 1)
  LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    this.m_secs = (pstm.ReadWord() << 16) >> 16;
    this.m_wf = pstm.ReadWord();
    this.m_tLast = pstm.ReadDword() | 0;
    this.m_szFormat = pstm.ReadString(80);
    this.m_fVisible = (this.m_wf & kfCtVisibleAtStart) !== 0; // SimUI.cpp:331 re-show
    return pstm.IsSuccess();
  }

  SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    pstm.WriteWord(this.m_secs);
    // triggermgr.cpp:516-518 — refresh kfCtVisibleAtStart from the (modeled) control
    this.m_wf = this.m_fVisible ? this.m_wf | kfCtVisibleAtStart : this.m_wf & ~kfCtVisibleAtStart;
    pstm.WriteWord(this.m_wf);
    pstm.WriteDword(this.m_tLast);
    pstm.WriteString(this.m_szFormat);
    return pstm.IsSuccess();
  }

  SetTimer(csecs: number, pszFormatString: string): void {
    this.m_secs = csecs;
    this.m_szFormat = pszFormatString;
  }

  StartTimer(fStart: boolean): void {
    this.m_wf = this.m_wf & ~kfCtRunning;
    if (fStart) {
      this.m_wf |= kfCtRunning;
      this.m_tLast = this.ctx.sim!.GetTickCount();
      this.ctx.host.UpdateCountdownString(this.m_secs, this.m_szFormat);
    }
  }

  ShowTimer(fShow: boolean): void {
    this.m_fVisible = fShow; // the modeled kfCtlVisible bit (saved via kfCtVisibleAtStart)
    this.ctx.host.ShowCountdownTimer(fShow);
  }

  GetTimer(): { secs: number; running: boolean } {
    return { secs: this.m_secs, running: (this.m_wf & kfCtRunning) !== 0 };
  }

  // triggermgr.cpp:550-569 — one second per 100 ticks, carrying m_tLast forward
  Update(): void {
    if (this.m_wf & kfCtRunning && this.m_secs > 0) {
      const t = this.ctx.sim!.GetTickCount();
      const dt = t - this.m_tLast;
      if (dt > 100) {
        this.m_secs -= 1;
        this.m_tLast += 100;
        this.ctx.host.UpdateCountdownString(this.m_secs, this.m_szFormat);
      }
    }
  }
}

//
// Trigger (triggermgr.cpp:268-455)
//

export class Trigger {
  m_pcdn: Condition[] = []; // linked list -> ordered array (link-in-last)
  m_pactn: TriggerAction[] = [];
  m_apactnLast: (TriggerAction | null)[] = new Array(kcSides).fill(null);
  m_afArmed: boolean[] = new Array(kcSides).fill(true);

  // triggermgr.cpp:356-394 — consume C/A props until the next 'T' (the caller stops us
  // by slicing the prop window; we just eat everything given)
  Init(ctx: SimContext, props: { key: string; value: string }[]): boolean {
    for (const prop of props) {
      switch (prop.key[0]) {
        case 'C':
          if (!LoadCondition(ctx, this, prop.value)) return false;
          break;
        case 'A':
          if (!LoadAction(ctx, this, prop.value)) return false;
          break;
        default:
          return false; // C++ asserts
      }
    }
    return true;
  }

  // triggermgr.cpp:396-455 — resume a blocking action, or check conditions then run
  Execute(ctx: SimContext, side: number, fForce = false): void {
    const pactnNext = this.m_apactnLast[side];
    if (pactnNext === null) {
      if (!fForce) {
        if (!this.m_afArmed[side]) return;
        for (const pcdn of this.m_pcdn) {
          if (!pcdn.IsTrue(ctx, side)) return;
        }
      }
      // Conditions met: disarm so the actions only happen once (PreserveTriggerAction
      // re-arms)
      this.m_afArmed[side] = false;
    }

    let i = pactnNext === null ? 0 : this.m_pactn.indexOf(pactnNext);
    for (; i < this.m_pactn.length; i++) {
      const pactn = this.m_pactn[i];
      this.m_apactnLast[side] = pactn;
      if (!pactn.Perform(ctx, this, side)) return;
      this.m_apactnLast[side] = null;
    }
  }

  // triggermgr.cpp:285-353 (knVerTriggerState 1) — the in-progress action per side is
  // saved as its list index ((char)-1 = none); armed flags as a raw bool[kcSides] RLE
  LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    for (let i = 0; i < kcSides; i++) {
      const nAction = (pstm.ReadByte() << 24) >> 24;
      this.m_apactnLast[i] = nAction !== -1 ? this.m_pactn[nAction] : null;
    }
    const ab = new Uint8Array(kcSides);
    pstm.ReadBytesRLE(ab);
    for (let i = 0; i < kcSides; i++) this.m_afArmed[i] = ab[i] !== 0;
    for (const pactn of this.m_pactn) pactn.LoadState(pstm);
    return pstm.IsSuccess();
  }

  SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    for (let i = 0; i < kcSides; i++) {
      const last = this.m_apactnLast[i];
      pstm.WriteByte(last === null ? 0xff : this.m_pactn.indexOf(last));
    }
    const ab = new Uint8Array(kcSides);
    for (let i = 0; i < kcSides; i++) ab[i] = this.m_afArmed[i] ? 1 : 0;
    pstm.WriteBytesRLE(ab);
    for (const pactn of this.m_pactn) pactn.SaveState(pstm);
    return pstm.IsSuccess();
  }

  SetCurrentActionComplete(side: number): void {
    this.m_apactnLast[side] = null;
  }
  Arm(side: number): void {
    this.m_afArmed[side] = true;
  }
}

//
// TriggerMgr (triggermgr.cpp:22-260 + ht.h:4934-5018)
//

export class TriggerMgr {
  private ctx: SimContext;
  m_atgr: Trigger[] = [];
  m_mpSide2nTrigger: number[][] = Array.from({ length: kcSides }, () =>
    new Array(kcTriggersPerSide).fill(0xff),
  );
  m_asidmCondition: number[] = new Array(knConditionMax).fill(0);
  m_abSwitch: number[] = new Array(kcSwitchMax).fill(0);
  m_cTimers = 0;
  m_actPeriod: number[] = new Array(kcTriggerTimersMax).fill(0);
  m_actCountdown: number[] = new Array(kcTriggerTimersMax).fill(0);
  m_tLastUpdate = 0;
  m_fEnabled = true;
  m_cdt: CountdownTimer;

  constructor(ctx: SimContext) {
    this.ctx = ctx;
    this.m_cdt = new CountdownTimer(ctx);
  }

  // triggermgr.cpp:39-105 — Count, then per trigger: a 'T' prop (side:index pairs)
  // followed by its C/A props (everything until the next 'T')
  Init(pini: IniReader): boolean {
    const ctgr = pini.GetInt('Triggers', 'Count');
    if (ctgr === null) return false;

    const sec = pini.GetSection('Triggers');
    if (sec === null) return false;
    // skip the Count property, then split into per-trigger prop runs at each 'T'
    const props = sec.props.filter((p) => p.key !== 'Count');
    let ntgr = 0;
    let i = 0;
    while (ntgr < ctgr) {
      if (i >= props.length || props[i].key !== 'T') return false;
      const tProp = props[i++];
      const tgr = new Trigger();
      if (!this.AssignTriggerSides(ntgr, tProp.value)) return false;
      const run: { key: string; value: string }[] = [];
      while (i < props.length && props[i].key !== 'T') run.push(props[i++]);
      if (!tgr.Init(this.ctx, run)) return false;
      this.m_atgr.push(tgr);
      ntgr++;
    }

    this.m_tLastUpdate = this.ctx.sim!.GetTickCount();
    return true;
  }

  // triggermgr.cpp:174-199 — "side:index[,side:index...]" pairs
  AssignTriggerSides(ntgr: number, psz: string): boolean {
    let rest = psz;
    while (true) {
      const r = IniScanf(rest, '%d:%d,%+');
      if (r.count === 0) break;
      if (r.count !== 2 && r.count !== 3) return false;
      const side = r.values[0] as number;
      const nIndex = r.values[1] as number;
      if (r.count === 3) rest = rest.slice(r.values[2] as number);
      if (nIndex < kcTriggersPerSide) this.m_mpSide2nTrigger[side][nIndex] = ntgr;
      if (r.count === 2) break;
    }
    return true;
  }

  // triggermgr.cpp:201-249
  Update(): void {
    if (!this.m_fEnabled) return;

    this.m_cdt.Update();

    const t = this.ctx.sim!.GetTickCount();
    const dt = t - this.m_tLastUpdate;
    this.m_tLastUpdate = t;

    // BUG-COMPAT (sim-quirks A15): triggermgr.cpp:211-215 `if (*pct != kctTimerNotStarted)
    // *pct++ -= dt;` advances the pointer ONLY inside the if-body — the first not-yet-
    // started timer stalls the scan and FREEZES every later-indexed timer that update.
    let ipct = 0;
    for (let i = 0; i < this.m_cTimers; i++) {
      if (this.m_actCountdown[ipct] !== kctTimerNotStarted) this.m_actCountdown[ipct++] -= dt;
    }

    for (let side = ksideNeutral; side < kcSides; side++) {
      const pntgr = this.m_mpSide2nTrigger[side];
      for (let j = 0; pntgr[j] !== 0xff; j++) {
        // a prior trigger may have disabled the rest (e.g. End Mission)
        if (this.m_fEnabled) this.m_atgr[pntgr[j]].Execute(this.ctx, side);
      }
    }

    this.m_asidmCondition.fill(0);

    for (let i = 0; i < this.m_cTimers; i++) {
      if (this.m_actCountdown[i] <= 0 && this.m_actCountdown[i] !== kctTimerNotStarted) {
        this.m_actCountdown[i] += this.m_actPeriod[i];
      }
    }
  }

  // triggermgr.cpp:107-172 (knVerTriggerMgrState 2) — runtime trigger state over the
  // already-parsed INI structure (Init runs first, as in Level::LoadState)
  LoadState(pstm: import('../sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 2) return false;
    for (const tgr of this.m_atgr) tgr.LoadState(pstm);

    const sw = new Uint8Array(kcSwitchMax);
    pstm.ReadBytesRLE(sw);
    for (let i = 0; i < kcSwitchMax; i++) this.m_abSwitch[i] = sw[i];

    // one-update condition sidemasks: raw SideMask[knConditionMax] (LE words)
    const cond = new Uint8Array(knConditionMax * 2);
    pstm.ReadBytesRLE(cond);
    for (let i = 0; i < knConditionMax; i++) {
      this.m_asidmCondition[i] = cond[i * 2] | (cond[i * 2 + 1] << 8);
    }

    for (let i = 0; i < this.m_cTimers; i++) this.m_actCountdown[i] = pstm.ReadDword() | 0;
    this.m_tLastUpdate = pstm.ReadDword() | 0;
    this.m_cdt.LoadState(pstm);
    return pstm.IsSuccess();
  }

  SaveState(pstm: import('../sm/stream.ts').Stream): boolean {
    pstm.WriteByte(2);
    for (const tgr of this.m_atgr) tgr.SaveState(pstm);

    const sw = new Uint8Array(kcSwitchMax);
    for (let i = 0; i < kcSwitchMax; i++) sw[i] = this.m_abSwitch[i];
    pstm.WriteBytesRLE(sw);

    const cond = new Uint8Array(knConditionMax * 2);
    for (let i = 0; i < knConditionMax; i++) {
      cond[i * 2] = this.m_asidmCondition[i] & 0xff;
      cond[i * 2 + 1] = (this.m_asidmCondition[i] >> 8) & 0xff;
    }
    pstm.WriteBytesRLE(cond);

    for (let i = 0; i < this.m_cTimers; i++) pstm.WriteDword(this.m_actCountdown[i]);
    pstm.WriteDword(this.m_tLastUpdate);
    this.m_cdt.SaveState(pstm);
    return pstm.IsSuccess();
  }

  SetConditionTrue(nCondition: number, sidm: number): void {
    this.m_asidmCondition[nCondition] |= sidm;
  }

  IsConditionTrue(nCondition: number, sidm: number): boolean {
    return (this.m_asidmCondition[nCondition] & sidm) !== 0;
  }

  GetCountdownTimer(): CountdownTimer {
    return this.m_cdt;
  }
  SetSwitch(iSwitch: number, fOn: boolean): void {
    this.m_abSwitch[iSwitch] = fOn ? 1 : 0;
  }
  GetSwitch(iSwitch: number): boolean {
    return this.m_abSwitch[iSwitch] !== 0;
  }

  // ht.h:4960-4978 — periodic timers only start counting the first time they're tested
  AddPeriodicTimer(ctPeriod: number): number {
    this.m_actPeriod[this.m_cTimers] = ctPeriod;
    this.m_actCountdown[this.m_cTimers] = kctTimerNotStarted;
    return this.m_cTimers++;
  }

  StartPeriodicTimer(iTimer: number): void {
    if (this.m_actCountdown[iTimer] === kctTimerNotStarted) {
      this.m_actCountdown[iTimer] = this.m_actPeriod[iTimer];
    }
  }

  IsPeriodicTimerTriggered(iTimer: number): boolean {
    return this.m_actCountdown[iTimer] <= 0;
  }

  Enable(fEnable: boolean): void {
    this.m_fEnabled = fEnable;
  }
}
